/** @jsxImportSource @emotion/react */
import {
  EuiBasicTable,
  EuiButton,
  EuiButtonEmpty,
  EuiButtonIcon,
  euiDragDropReorder,
  EuiFlexGroup,
  EuiForm,
  EuiFormRow,
  EuiHighlight,
  EuiIcon,
  EuiInputPopover,
  EuiModal,
  EuiModalBody,
  EuiModalFooter,
  EuiModalHeader,
  EuiModalHeaderTitle,
  EuiSelectable,
  EuiSelectableOption,
  EuiSpacer,
  EuiTableComputedColumnType,
  EuiToken,
  htmlIdGenerator,
  useEuiTheme,
} from "@inscopix/ideas-eui";
import { css } from "@emotion/react";
import { useState } from "react";
import { useDataTableContext } from "../store/DataTableProvider";
import { COLUMN_KIND_MAP } from "./ModalCreateColumn/ModalCreateColumn";
import { DataTableColumnDefinition } from "../store/DataTableProvider.types";
import { chain } from "lodash";
import assert from "assert";
import { addUtilityToastSuccess } from "utils/addUtilityToastSuccess";
import { addUtilityToastFailure } from "utils/addUtilityToastFailure";
import { isDefined } from "utils/isDefined";
import { isNonNull } from "utils/isNonNull";

interface ModalEditRowIdentifierFormatProps {
  onClose: () => void;
  tableId: string;
}

export const ModalEditRowIdentifierFormat = ({
  onClose,
  tableId,
}: ModalEditRowIdentifierFormatProps) => {
  const { euiTheme } = useEuiTheme();

  const setIdentifierPositions = useDataTableContext(
    (s) => s.setIdentifierPositions,
  );

  const columns: EuiTableComputedColumnType<{
    id: string;
    index: number;
    name: string;
    group?: string;
    kind: DataTableColumnDefinition["kind"];
    onMoveUp: () => void;
    onMoveDown: () => void;
    onRemove: () => void;
  }>[] = [
    {
      width: "70px",
      render: ({ onMoveUp, onMoveDown }) => {
        return (
          <div>
            <EuiButtonIcon
              aria-label="Move up"
              iconType="arrowUp"
              onClick={onMoveUp}
            />
            <EuiButtonIcon
              aria-label="Move down"
              iconType="arrowDown"
              onClick={onMoveDown}
            />
          </div>
        );
      },
    },
    {
      name: "Name",
      render: ({ name, group }) =>
        isDefined(group) ? (
          <>
            <span style={{ color: euiTheme.colors.subduedText }}>
              {group}
              <EuiIcon
                type="arrowRight"
                size="s"
                css={css`
                  margin: 0 4px 2px 4px;
                  color: ${euiTheme.colors.subduedText};
                `}
              />
            </span>
            {` ${name}`}
          </>
        ) : (
          name
        ),
    },
    {
      name: "Type",
      width: "160px",
      render: ({ kind }) => {
        return (
          <EuiFlexGroup alignItems="center" gutterSize="s">
            <EuiToken iconType={COLUMN_KIND_MAP[kind].icon} />
            <span>{COLUMN_KIND_MAP[kind].name}</span>
          </EuiFlexGroup>
        );
      },
    },
    {
      width: "50px",
      render: ({ onRemove }) => {
        return (
          <EuiButtonIcon
            aria-label="Remove"
            color="danger"
            iconType="cross"
            onClick={onRemove}
          />
        );
      },
    },
  ];

  // Calculate the initial column identifier positions
  const initialItemIds = useDataTableContext((s) => {
    const table = s.tables.find(({ id }) => id === tableId);
    const itemIds: string[] = [];
    table?.columns.forEach((column) => {
      if (column.identifier_position !== null) {
        itemIds[column.identifier_position] = column.id;
      }
    });
    return itemIds.filter(isDefined);
  });

  const [isOptionListOpen, setIsOptionListOpen] = useState(false);
  const [inputValue, setInputValue] = useState("");

  const [isLoading, setIsLoading] = useState(false);
  const [formId] = useState(htmlIdGenerator()());
  const [itemIds, setItemIds] = useState(initialItemIds);

  // make it easy to look up group name from ID
  const groupsById = useDataTableContext((s) => {
    const table = s.tables.find(({ id }) => id === tableId);
    return chain(table?.columnGroups ?? [])
      .keyBy((group) => group.id)
      .value();
  });

  // Format table items as a lookup map
  const itemsById = useDataTableContext((s) => {
    const table = s.tables.find(({ id }) => id === tableId);
    return chain(table?.columns ?? [])
      .filter((column) => !column.is_id_column)
      .map((column) => ({
        id: column.id,
        name: column.name,
        isIdColumn: column.is_id_column,
        kind: column.definition.kind,
        group: isNonNull(column.group)
          ? groupsById[column.group]?.name
          : undefined,
      }))
      .keyBy((column) => column.id)
      .value();
  });

  const handleSubmit = async () => {
    try {
      setIsLoading(true);
      const { error } = await setIdentifierPositions({
        tableId,
        columnIds: itemIds,
      });
      assert(error === undefined);
      addUtilityToastSuccess("Row identifier format updated");
    } catch (error) {
      addUtilityToastFailure("Failed to update row identifier format");
    } finally {
      setIsLoading(false);
      onClose();
    }
  };

  const renderOption = (
    option: EuiSelectableOption<{ group?: string }>,
    searchValue: string,
  ) => {
    return (
      <>
        {isDefined(option.group) && (
          <span style={{ color: euiTheme.colors.subduedText }}>
            <EuiHighlight search={searchValue}>{option.group}</EuiHighlight>
            <EuiIcon
              type="arrowRight"
              size="s"
              css={css`
                margin: 0 4px 2px 4px;
                color: ${euiTheme.colors.subduedText};
              `}
            />
          </span>
        )}
        <EuiHighlight search={searchValue}>{option.label}</EuiHighlight>
      </>
    );
  };

  return (
    <EuiModal
      onClose={onClose}
      css={css`
        max-width: 1200px;
        width: 80vw;
        min-width: 600px;
        overflow-y: scroll;
      `}
    >
      <EuiModalHeader>
        <EuiModalHeaderTitle component="h3">
          Row Identifier Format
        </EuiModalHeaderTitle>
      </EuiModalHeader>

      <EuiForm
        id={formId}
        component="form"
        role="form"
        onSubmit={(e) => {
          e.preventDefault();
          void handleSubmit();
        }}
      >
        <EuiModalBody>
          <EuiFormRow
            fullWidth
            label="Included columns"
            helpText="Cell values in these columns will appear in row identifiers. Reorder the columns to change the order in which cell values will appear."
          >
            <>
              {itemIds.length > 0 && (
                <EuiBasicTable
                  columns={columns}
                  itemId={(item) => item.id}
                  items={itemIds.map((id, idx, arr) => {
                    const item = itemsById[id];
                    return {
                      ...item,
                      index: idx + 1,
                      onMoveUp: () => {
                        if (idx > 0) {
                          setItemIds((prev) =>
                            euiDragDropReorder(prev, idx, idx - 1),
                          );
                        }
                      },
                      onMoveDown: () => {
                        if (idx < arr.length - 1) {
                          setItemIds((prev) =>
                            euiDragDropReorder(prev, idx, idx + 1),
                          );
                        }
                      },
                      onRemove: () =>
                        setItemIds((prev) =>
                          prev.filter((id) => id !== item.id),
                        ),
                    };
                  })}
                />
              )}
              <EuiSpacer size="s" />
              <EuiSelectable
                aria-label="Add a column"
                options={Object.values(itemsById).map((item) => ({
                  key: item.id,
                  value: item.id,
                  label: item.name,
                  searchableLabel: `${item.group ?? ""} ${item.name}`,
                  checked: itemIds.includes(item.id) ? "on" : undefined,
                  group: item.group,
                }))}
                renderOption={renderOption}
                onChange={(options) => {
                  const itemIds = options
                    .filter((option) => option.checked === "on")
                    .map((option) => option.value);
                  setItemIds(itemIds);
                }}
                searchable
                searchProps={{
                  placeholder: "Select columns from this table...",
                  fullWidth: true,
                  value: inputValue,
                  onChange: (value) => {
                    setInputValue(value);
                  },
                  onKeyDown: (event) => {
                    if (event.key === "Tab") return setIsOptionListOpen(false);
                    if (event.key !== "Escape") {
                      return setIsOptionListOpen(true);
                    }
                  },
                  onClick: () => setIsOptionListOpen(true),
                  onFocus: () => setIsOptionListOpen(true),
                }}
                isPreFiltered={false}
                noMatchesMessage="No matching columns found"
                emptyMessage="No columns available in table that can be used for row identifiers"
              >
                {(list, search) => (
                  <EuiInputPopover
                    closePopover={() => setIsOptionListOpen(false)}
                    disableFocusTrap
                    closeOnScroll
                    isOpen={isOptionListOpen}
                    input={search ?? ""}
                    panelPaddingSize="none"
                    fullWidth
                  >
                    {list}
                  </EuiInputPopover>
                )}
              </EuiSelectable>
            </>
          </EuiFormRow>
        </EuiModalBody>
      </EuiForm>

      <EuiModalFooter>
        <EuiButtonEmpty onClick={onClose}>Cancel</EuiButtonEmpty>
        <EuiButton type="submit" form={formId} isLoading={isLoading} fill>
          Update
        </EuiButton>
      </EuiModalFooter>
    </EuiModal>
  );
};
